import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ReportGenerator = ({ onGenerateReport, onScheduleReport }) => {
  const [reportConfig, setReportConfig] = useState({
    type: 'performance',
    format: 'pdf',
    includeCharts: true,
    includeDetails: true,
    includeComparison: false,
    scheduledFrequency: 'none',
    recipients: ''
  });

  const reportTypeOptions = [
    { value: 'performance', label: 'Performance Summary' },
    { value: 'compliance', label: 'Compliance Report' },
    { value: 'stakeholder', label: 'Stakeholder Analysis' },
    { value: 'transaction', label: 'Transaction Overview' },
    { value: 'audit', label: 'Audit Log Export' },
    { value: 'custom', label: 'Custom Report' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF Document' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'csv', label: 'CSV Data' },
    { value: 'json', label: 'JSON Export' }
  ];

  const frequencyOptions = [
    { value: 'none', label: 'One-time Report' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const handleConfigChange = (key, value) => {
    setReportConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleGenerateReport = () => {
    onGenerateReport(reportConfig);
  };

  const handleScheduleReport = () => {
    if (reportConfig?.scheduledFrequency !== 'none') {
      onScheduleReport(reportConfig);
    }
  };

  const scheduledReports = [
    {
      id: 1,
      name: "Weekly Performance Report",
      type: "performance",
      frequency: "weekly",
      nextRun: "2025-09-28",
      recipients: "admin@propertychain.com, manager@propertychain.com"
    },
    {
      id: 2,
      name: "Monthly Compliance Summary",
      type: "compliance",
      frequency: "monthly",
      nextRun: "2025-10-01",
      recipients: "compliance@propertychain.com"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Report Generator */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Generate Report</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Select
            label="Report Type"
            options={reportTypeOptions}
            value={reportConfig?.type}
            onChange={(value) => handleConfigChange('type', value)}
          />

          <Select
            label="Export Format"
            options={formatOptions}
            value={reportConfig?.format}
            onChange={(value) => handleConfigChange('format', value)}
          />
        </div>

        <div className="space-y-4 mb-6">
          <div className="flex items-center space-x-6">
            <Checkbox
              label="Include Charts & Graphs"
              checked={reportConfig?.includeCharts}
              onChange={(e) => handleConfigChange('includeCharts', e?.target?.checked)}
            />
            <Checkbox
              label="Include Detailed Data"
              checked={reportConfig?.includeDetails}
              onChange={(e) => handleConfigChange('includeDetails', e?.target?.checked)}
            />
            <Checkbox
              label="Include Period Comparison"
              checked={reportConfig?.includeComparison}
              onChange={(e) => handleConfigChange('includeComparison', e?.target?.checked)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Report will include data from current filter settings
          </div>
          <Button
            variant="default"
            onClick={handleGenerateReport}
            iconName="Download"
            iconPosition="left"
          >
            Generate Report
          </Button>
        </div>
      </div>
      {/* Schedule Reports */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Calendar" size={20} className="text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Schedule Automated Reports</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Select
            label="Frequency"
            options={frequencyOptions}
            value={reportConfig?.scheduledFrequency}
            onChange={(value) => handleConfigChange('scheduledFrequency', value)}
          />

          <Input
            label="Email Recipients"
            type="email"
            placeholder="email1@domain.com, email2@domain.com"
            value={reportConfig?.recipients}
            onChange={(e) => handleConfigChange('recipients', e?.target?.value)}
            description="Separate multiple emails with commas"
          />
        </div>

        <div className="flex items-center justify-end pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={handleScheduleReport}
            disabled={reportConfig?.scheduledFrequency === 'none'}
            iconName="Clock"
            iconPosition="left"
          >
            Schedule Report
          </Button>
        </div>
      </div>
      {/* Scheduled Reports List */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Scheduled Reports</h3>
          <Button variant="ghost" size="sm" iconName="Settings" iconPosition="left">
            Manage All
          </Button>
        </div>

        <div className="space-y-4">
          {scheduledReports?.map((report) => (
            <div key={report?.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="text-sm font-medium text-foreground">{report?.name}</h4>
                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                    {report?.frequency}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <span>Next run: {new Date(report.nextRun)?.toLocaleDateString()}</span>
                  <span>•</span>
                  <span>Recipients: {report?.recipients?.split(',')?.length} users</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button variant="ghost" size="sm" iconName="Edit" />
                <Button variant="ghost" size="sm" iconName="Pause" />
                <Button variant="ghost" size="sm" iconName="Trash2" className="text-error hover:text-error" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportGenerator;